// Overview.jsx

import { useEffect, useState } from "react";
import { SweetAlertShow } from "../../../master/Master";
import { studentDetailsOverview } from "../../../../api/StudentDetails";

const Skeleton = ({ w = "100%", h = "14px" }) => (
  <span
    className="placeholder"
    style={{
      width: w,
      height: h,
      display: "inline-block",
      borderRadius: "6px",
    }}
  ></span>
);

const StatCard = ({ icon, color, label, value, sub, loading }) => (
  <div className="col-lg-3 col-md-6">
    <div
      className="p-3 bg-white rounded-3 h-100"
      style={{ border: "1px solid #eef0f4" }}
    >
      <div className="d-flex align-items-center gap-2 mb-3">
        <div
          className={`d-flex align-items-center justify-content-center rounded-3 bg-${color} bg-opacity-10`}
          style={{ width: 42, height: 42 }}
        >
          <i className={`${icon} text-${color} fs-4`}></i>
        </div>
        <small className="text-secondary fw-medium">{label}</small>
      </div>

      {loading ? (
        <div className="placeholder-glow">
          <Skeleton w="80px" h="26px" />
        </div>
      ) : (
        <h4 className="mb-1 fw-bold">{value}</h4>
      )}

      <small className={`text-${color}`}>{sub}</small>
    </div>
  </div>
);

const InfoCard = ({ icon, color, title, rows, loading }) => (
  <div className="col-lg-4">
    <div
      className="p-3 bg-white rounded-3 h-100"
      style={{ border: "1px solid #eef0f4" }}
    >
      <div className="d-flex align-items-center gap-2 mb-3">
        <div
          className={`d-flex align-items-center justify-content-center rounded-circle bg-${color} bg-opacity-10`}
          style={{ width: 34, height: 34 }}
        >
          <i
            className={`${icon} text-${color === "primary" ? "white" : color}`}
          ></i>
        </div>
        <span className="fw-semibold">{title}</span>
      </div>

      {loading ? (
        <div className="placeholder-glow d-flex flex-column gap-2">
          <Skeleton h="15px" />
          <Skeleton h="15px" />
          <Skeleton h="15px" />
          <Skeleton h="15px" />
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {rows.map((r, i) => (
            <div
              key={i}
              className="d-flex justify-content-between align-items-center py-1"
              style={{
                borderBottom:
                  i !== rows.length - 1 ? "1px dashed #f0f0f0" : "none",
              }}
            >
              <small className="text-secondary d-flex align-items-center gap-1">
                <i className={`${r.icon} text-${color}`}></i> {r.label}
              </small>
              <small className="fw-medium">{r.value ?? "-"}</small>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

const FacilityCard = ({ icon, color, title, active, rows, loading }) => (
  <div className="col-lg-4">
    <div
      className="p-3 bg-white rounded-3 h-100"
      style={{ border: "1px solid #eef0f4" }}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <div
            className={`d-flex align-items-center justify-content-center rounded-3 bg-${color} bg-opacity-10`}
            style={{ width: 34, height: 34 }}
          >
            <i
              className={`${icon} text-${color === "primary" ? "white" : color}`}
            ></i>
          </div>
          <span className="fw-semibold">{title}</span>
        </div>
        {!loading && rows && (
          <span
            className={`badge rounded-pill bg-${active ? "success" : "secondary"} bg-opacity-10 text-${active ? "success" : "secondary"}`}
          >
            <i
              className="ri-checkbox-blank-circle-fill me-1"
              style={{ fontSize: 6 }}
            ></i>
            {active ? "Active" : "Inactive"}
          </span>
        )}
      </div>

      {loading ? (
        <div className="placeholder-glow d-flex flex-column gap-2">
          <Skeleton h="14px" />
          <Skeleton h="14px" />
        </div>
      ) : rows ? (
        <div className="d-flex flex-column gap-2">
          {rows.map((r, i) => (
            <small
              key={i}
              className="text-secondary d-flex justify-content-between"
            >
              <span>{r.label}</span>
              <span className="fw-medium text-dark">{r.value || "-"}</span>
            </small>
          ))}
        </div>
      ) : (
        <div className="text-center py-3 text-muted">
          <i className="ri-forbid-line d-block fs-3 mb-1"></i>
          <small>Not assigned</small>
        </div>
      )}
    </div>
  </div>
);

export default function Overview({ id }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const getStudentDetailsMain = async (student_id) => {
    setLoading(true);
    try {
      const response = await studentDetailsOverview(student_id);
      if (response.data.status) {
        setData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudentDetailsMain(id);
  }, [id]);

  const attendance = data?.attendances?.[0];
  const attendancePct = attendance?.total
    ? Math.round((Number(attendance.present) / attendance.total) * 100)
    : 0;

  const hostel = data?.student_hostel_info;
  const transport = data?.student_transport_info;
  const library = data?.student_library_info;
  const lastTxn = data?.last_transaction;

  const batchFee = data?.student_batch_n_fees?.[0] || {};
  const hostelFee = data?.student_hostel_n_fees?.[0] || {};
  const transportFee = data?.student_transport_n_fees?.[0] || {};
  const libraryFee = data?.student_library_n_fees?.[0] || {};

  const totalFee =
    Number(batchFee.final_fee || 0) +
    Number(hostelFee.final_fee || 0) +
    Number(transportFee.final_fee || 0) +
    Number(libraryFee.final_fee || 0);

  const paidFee =
    Number(batchFee.total_paid || 0) +
    Number(hostelFee.total_paid || 0) +
    Number(transportFee.total_paid || 0) +
    Number(libraryFee.total_paid || 0);

  const dueFee =
    Number(batchFee.total_due || 0) +
    Number(hostelFee.total_due || 0) +
    Number(transportFee.total_due || 0) +
    Number(libraryFee.total_due || 0);

  const paidFeePercent =
    totalFee > 0 ? Math.round((paidFee / totalFee) * 100) : 0;

  return (
    <div className="row g-3">
      {/* Summary Cards */}
      <StatCard
        icon="ri-wallet-3-line"
        color="warning"
        label="Payable Fee"
        loading={loading}
        value={`₹${totalFee?.toLocaleString()}`}
        sub={
          <>
            <i className="ri-arrow-right-up-line"></i> Overall
          </>
        }
      />

      <StatCard
        icon="ri-money-dollar-circle-line"
        color="success"
        label="Received Fee"
        loading={loading}
        value={`₹${paidFee?.toLocaleString()}`}
        sub={
          <>
            <i className="ri-arrow-right-up-line"></i> {paidFeePercent}% Paid
          </>
        }
      />

      <StatCard
        icon="ri-error-warning-line"
        color="danger"
        label="Due Fee"
        loading={loading}
        value={`₹${dueFee?.toLocaleString()}`}
        sub={
          <>
            <i className="ri-arrow-right-down-line"></i> Pending
          </>
        }
      />

      <div className="col-lg-3 col-md-6">
        <div
          className="p-3 bg-white rounded-3 h-100"
          style={{ border: "1px solid #eef0f4" }}
        >
          <div className="d-flex align-items-center gap-2 mb-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 bg-primary bg-opacity-10"
              style={{ width: 42, height: 42 }}
            >
              <i className="ri-calendar-check-line text-white fs-4"></i>
            </div>
            <small className="text-secondary fw-medium">Attendance</small>
          </div>

          {loading ? (
            <div className="placeholder-glow">
              <Skeleton w="60px" h="26px" />
            </div>
          ) : (
            <>
              <h4 className="mb-2 fw-bold">{attendancePct}%</h4>
              <div className="progress" style={{ height: 6 }}>
                <div
                  className={`progress-bar bg-${attendancePct >= 75 ? "success" : "danger"}`}
                  style={{ width: `${attendancePct}%` }}
                ></div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <InfoCard
        icon="ri-user-3-line"
        color="primary"
        title="Student Information"
        loading={loading}
        rows={[
          { icon: "ri-user-line", label: "Name", value: data?.name },
          {
            icon: "ri-id-card-line",
            label: "Registration",
            value: data?.registration_no,
          },
          {
            icon: "ri-book-open-line",
            label: "Course",
            value: data?.student_batch_info?.course?.title,
          },
          {
            icon: "ri-team-line",
            label: "Batch",
            value: data?.student_batch_info?.batch?.title,
          },
        ]}
      />

      <InfoCard
        icon="ri-parent-line"
        color="info"
        title="Parent Details"
        loading={loading}
        rows={[
          { icon: "ri-men-line", label: "Father", value: data?.father_name },
          { icon: "ri-women-line", label: "Mother", value: data?.mother_name },
          { icon: "ri-phone-line", label: "Phone", value: data?.phone },
          { icon: "ri-mail-line", label: "Email", value: data?.email },
        ]}
      />

      <InfoCard
        icon="ri-graduation-cap-line"
        color="success"
        title="Academic Summary"
        loading={loading}
        rows={[
          {
            icon: "ri-calendar-line",
            label: "Session",
            value: data?.student_batch_info?.session_year?.session,
          },
          { icon: "ri-genderless-line", label: "Gender", value: data?.gender },
          {
            icon: "ri-checkbox-circle-line",
            label: "Present",
            value: attendance?.present,
          },
          {
            icon: "ri-close-circle-line",
            label: "Absent",
            value: attendance?.absent,
          },
        ]}
      />

      {/* Facilities */}
      <FacilityCard
        icon="ri-hotel-bed-line"
        color="danger"
        title="Hostel"
        loading={loading}
        active={hostel?.status === "1"}
        rows={
          hostel && [
            { label: "Room", value: hostel?.room?.room_no },
            { label: "Bed", value: hostel?.bed?.bed_no },
            { label: "Hostel", value: hostel?.hostel?.name },
          ]
        }
      />

      <FacilityCard
        icon="ri-bus-2-line"
        color="primary"
        title="Transport"
        loading={loading}
        active={transport?.status === "1"}
        rows={
          transport && [
            { label: "Bus No", value: transport?.bus?.bus_number },
            { label: "Stop", value: transport?.stop?.stop_name },
          ]
        }
      />

      <FacilityCard
        icon="ri-book-open-line"
        color="success"
        title="Library"
        loading={loading}
        active={library?.status === "1"}
        rows={
          library && [
            { label: "Seat", value: library?.seat?.seat_no },
            { label: "Shift", value: library?.shift?.name },
            { label: "Library", value: library?.library?.name },
          ]
        }
      />

      {/* Recent Payment */}
      <div className="col-lg-6">
        <div
          className="p-3 bg-white rounded-3 h-100"
          style={{ border: "1px solid #eef0f4" }}
        >
          <div className="d-flex align-items-center gap-2 mb-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-10"
              style={{ width: 34, height: 34 }}
            >
              <i className="ri-money-dollar-circle-line text-warning"></i>
            </div>
            <span className="fw-semibold">Recent Payment</span>
          </div>

          {loading ? (
            <div className="placeholder-glow d-flex flex-column gap-2">
              <Skeleton h="40px" />
            </div>
          ) : lastTxn ? (
            <div
              className="d-flex justify-content-between align-items-center p-2 rounded-3"
              style={{ background: "#f8f9fb" }}
            >
              <div className="d-flex align-items-center gap-2">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10"
                  style={{ width: 38, height: 38 }}
                >
                  <i className="ri-arrow-right-up-line text-success"></i>
                </div>
                <div>
                  <div className="fw-medium">{lastTxn.receipt_no}</div>
                  <small className="text-secondary">
                    {new Date(lastTxn.payment_date).toLocaleDateString(
                      "en-IN",
                      { day: "2-digit", month: "short", year: "numeric" },
                    )}
                  </small>
                </div>
              </div>
              <span className="fw-bold text-success">
                ₹{Number(lastTxn.amount).toLocaleString()}
              </span>
            </div>
          ) : (
            <div className="text-center py-3 text-muted">
              <i className="ri-inbox-line d-block fs-3 mb-1"></i>
              <small>No payments found</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
